(function initPurchaseVatTreatment(global) {
  const STANDARD_RATES = [25, 15, 12, 0];
  const REVERSE_CHARGE_CODE = '86';
  const SPECIAL_CODES = ['14', '21', '23'];

  const STANDARD_DETAILS = {
    25: ['buy.vatChoice.rate25Context', 'Bilaget viser 25 % norsk MVA, og kjøpet gir fradragsrett.', 'buy.vatChoice.rate25Effect', 'Plumme skiller kostnaden fra inngående MVA og tar fradraget med i MVA-meldingen.'],
    15: ['buy.vatChoice.rate15Context', 'Bilaget viser 15 % norsk MVA, normalt ved kjøp av næringsmidler, og kjøpet gir fradragsrett.', 'buy.vatChoice.rate15Effect', 'Plumme skiller kostnaden fra inngående MVA og tar fradraget med i MVA-meldingen.'],
    12: ['buy.vatChoice.rate12Context', 'Bilaget viser 12 % norsk MVA, blant annet ved enkelte kjøp av persontransport eller overnatting, og kjøpet gir fradragsrett.', 'buy.vatChoice.rate12Effect', 'Plumme skiller kostnaden fra inngående MVA og tar fradraget med i MVA-meldingen.'],
    0: ['buy.vatChoice.rate0Context', 'Bilaget gir ikke fradrag for norsk inngående MVA.', 'buy.vatChoice.rate0Effect', 'Hele beløpet bokføres som kostnad eller eiendel. Ved vareimport registreres innførsels-MVA separat fra tolldeklarasjonen.'],
  };

  const CUSTOMS_CODES = [
    ['81', 'buy.vatChoice.customs81', 'Kode 81 · Vareimport, høy sats med fradrag', 'buy.vatChoice.customs81Context', 'Varene ville hatt 25 % MVA ved salg i Norge, og virksomheten har hel eller forholdsmessig fradragsrett.', 'buy.vatChoice.customs81Effect', 'Tolldeklarasjonen rapporterer både beregnet utgående MVA og fradragsberettiget inngående MVA.'],
    ['82', 'buy.vatChoice.customs82', 'Kode 82 · Vareimport, høy sats uten fradrag', 'buy.vatChoice.customs82Context', 'Varene ville hatt 25 % MVA ved salg i Norge, men virksomheten har ikke fradragsrett.', 'buy.vatChoice.customs82Effect', 'Tolldeklarasjonen beregner utgående MVA uten tilsvarende fradrag, og øker dermed MVA som skal betales.'],
    ['83', 'buy.vatChoice.customs83', 'Kode 83 · Vareimport, middels sats med fradrag', 'buy.vatChoice.customs83Context', 'Varene omfattes av middels sats, normalt 15 %, og virksomheten har hel eller forholdsmessig fradragsrett.', 'buy.vatChoice.customs83Effect', 'Tolldeklarasjonen rapporterer både beregnet utgående MVA og fradragsberettiget inngående MVA.'],
    ['84', 'buy.vatChoice.customs84', 'Kode 84 · Vareimport, middels sats uten fradrag', 'buy.vatChoice.customs84Context', 'Varene omfattes av middels sats, normalt 15 %, men virksomheten har ikke fradragsrett.', 'buy.vatChoice.customs84Effect', 'Tolldeklarasjonen beregner utgående MVA uten tilsvarende fradrag, og øker dermed MVA som skal betales.'],
    ['85', 'buy.vatChoice.customs85', 'Kode 85 · Vareimport uten beregnet MVA', 'buy.vatChoice.customs85Context', 'Tolldeklarasjonen gjelder varer det ikke skal beregnes innførsels-MVA av, for eksempel enkelte nullsatsede innførsler.', 'buy.vatChoice.customs85Effect', 'Grunnlaget rapporteres i MVA-meldingen uten at det beregnes MVA. Kontroller alltid varetype og prosedyre mot tolldeklarasjonen.'],
  ];

  const resolve = (value) => {
    const normalized = String(value || 'rate:0');
    if (normalized === 'vat-only:14') {
      return { vatRate: 25, reverseChargeCode: '', purchaseVatCode: '14', standardVatCode: '14', vatTreatment: 'vatOnly' };
    }
    if (normalized === 'import-basis:21') {
      return { vatRate: 25, reverseChargeCode: '', purchaseVatCode: '21', standardVatCode: '21', vatTreatment: 'importBasis' };
    }
    if (normalized === 'import-basis:23') {
      // Kode 23 er produktets synlige kode. Ved SAF-T-eksport mappes den til
      // Skatteetatens standardkode 20, som er gjeldende kode for import uten
      // MVA-behandling siden 2017.
      return { vatRate: 0, reverseChargeCode: '', purchaseVatCode: '23', standardVatCode: '20', vatTreatment: 'importBasis' };
    }
    if (normalized === `reverse:${REVERSE_CHARGE_CODE}`) {
      return { vatRate: 25, reverseChargeCode: REVERSE_CHARGE_CODE, vatTreatment: 'reverseCharge' };
    }
    const parsed = Number(normalized.replace(/^rate:/, ''));
    const vatRate = STANDARD_RATES.includes(parsed) ? parsed : 0;
    return { vatRate, reverseChargeCode: '', vatTreatment: vatRate > 0 ? 'standard' : 'zeroRated' };
  };

  const valueFor = ({ vatRate = 0, reverseChargeCode = '', purchaseVatCode = '', vatTreatment = '' } = {}) => {
    const code = String(purchaseVatCode || '');
    if (code === '14' || vatTreatment === 'vatOnly') return 'vat-only:14';
    if (code === '21') return 'import-basis:21';
    if (code === '23') return 'import-basis:23';
    return String(reverseChargeCode || '') === REVERSE_CHARGE_CODE
      ? `reverse:${REVERSE_CHARGE_CODE}`
      : `rate:${STANDARD_RATES.includes(Number(vatRate)) ? Number(vatRate) : 0}`;
  };

  const groups = ({ rates = STANDARD_RATES, includeReverse = true, includeSpecial = false } = {}) => {
    const allowedRates = STANDARD_RATES.filter((rate) => rates.map(Number).includes(rate));
    const labels = {
      25: ['buy.vatChoice.rate25', '25 % · Kjøp, høy sats', 'buy.vatChoice.rate25Hint', 'Brukes når bilaget fra en norsk selger viser 25 % MVA og kjøpet gir fradragsrett.'],
      15: ['buy.vatChoice.rate15', '15 % · Kjøp, middels sats', 'buy.vatChoice.rate15Hint', 'Brukes når bilaget viser 15 % MVA og kjøpet gir fradragsrett.'],
      12: ['buy.vatChoice.rate12', '12 % · Kjøp, lav sats', 'buy.vatChoice.rate12Hint', 'Brukes når bilaget viser 12 % MVA og kjøpet gir fradragsrett.'],
      0: ['buy.vatChoice.rate0', 'Ingen MVA-kode · ingen MVA-behandling', 'buy.vatChoice.rate0Hint', 'Brukes når kjøpet ikke skal ha fradrag for inngående MVA. Hele beløpet bokføres som kostnad eller eiendel.'],
    };
    const optionForRate = (rate) => ({
      value: `rate:${rate}`,
      labelKey: labels[rate][0],
      fallback: labels[rate][1],
      hintKey: labels[rate][2],
      hintFallback: labels[rate][3],
      contextKey: STANDARD_DETAILS[rate][0],
      contextFallback: STANDARD_DETAILS[rate][1],
      effectKey: STANDARD_DETAILS[rate][2],
      effectFallback: STANDARD_DETAILS[rate][3],
      scopeKey: 'buy.vatChoice.supplierDocument',
      scopeFallback: 'Leverandørbilag',
      selectable: true,
    });
    const result = [];
    if (allowedRates.includes(0)) result.push({
      key: 'none',
      labelKey: 'buy.vatChoice.noneGroup',
      fallback: 'Ingen MVA-kode',
      options: [optionForRate(0)],
    });
    const ordinaryRates = allowedRates.filter((rate) => rate > 0);
    if (ordinaryRates.length) result.push({
      key: 'standard',
      labelKey: 'buy.vatChoice.standardGroup',
      fallback: 'Vanlig kjøp',
      options: ordinaryRates.map(optionForRate),
    });
    if (includeReverse && allowedRates.includes(25)) {
      result.push({
        key: 'reverse',
        labelKey: 'buy.vatChoice.reverseGroup',
        fallback: 'Kjøp av tjenester fra utlandet',
        options: [{
          value: `reverse:${REVERSE_CHARGE_CODE}`,
          labelKey: 'buy.vatChoice.reverse86',
          fallback: 'Kode 86 · Tjenester fra utlandet (25 %)',
          hintKey: 'buy.vatChoice.reverse86Hint',
          hintFallback: 'Brukes for fjernleverbare tjenester kjøpt fra utlandet uten norsk MVA på fakturaen.',
          contextKey: 'buy.vatChoice.reverse86Context',
          contextFallback: 'Brukes for fjernleverbare tjenester fra en utenlandsk leverandør når fakturaen ikke viser norsk MVA.',
          effectKey: 'buy.vatChoice.reverse86Effect',
          effectFallback: 'Leverandørbeløpet er avgiftsgrunnlaget. Plumme beregner utgående MVA og eventuell fradragsberettiget inngående MVA.',
          scopeKey: 'buy.vatChoice.supplierDocument',
          scopeFallback: 'Leverandørbilag',
          selectable: true,
        }],
      });
    }
    if (includeSpecial) result.push({
      key: 'vatOnly',
      labelKey: 'buy.vatChoice.vatOnlyGroup',
      fallback: 'Kun MVA',
      options: [{
        value: 'vat-only:14',
        code: '14',
        labelKey: 'buy.vatChoice.vatOnly14',
        fallback: 'Kode 14 · Kun inngående MVA, høy sats',
        contextKey: 'buy.vatChoice.vatOnly14Context',
        contextFallback: 'Brukes bare i særtilfeller der 25 % innførsels-MVA er betalt til Tolletaten eller speditøren og beløpet skal føres direkte som fradragsberettiget inngående MVA.',
        effectKey: 'buy.vatChoice.vatOnly14Effect',
        effectFallback: 'Hele linjebeløpet føres direkte på konto for inngående MVA. Det opprettes ingen kostnad eller kategori på linjen.',
        scopeKey: 'buy.vatChoice.supplierDocument',
        scopeFallback: 'Leverandørbilag',
        selectable: true,
      }],
    });
    if (includeSpecial) result.push({
      key: 'importBasis',
      labelKey: 'buy.vatChoice.importBasisGroup',
      fallback: 'Kun grunnlag for MVA',
      options: [{
        value: 'import-basis:21', code: '21',
        labelKey: 'buy.vatChoice.importBasis21', fallback: 'Kode 21 · Grunnlag ved innførsel av varer (25 %)',
        contextKey: 'buy.vatChoice.importBasis21Context',
        contextFallback: 'Brukes på fakturaen fra utenlandsk selger for varer og tilleggsutgifter som frakt eller forsikring når varen omfattes av 25 % innførsels-MVA.',
        effectKey: 'buy.vatChoice.importBasis21Effect',
        effectFallback: 'Fakturaen bokføres som kostnad uten MVA. Når tolldeklarasjonen kommer, registreres innførselsgrunnlaget og avgiften separat med kode 81.',
        scopeKey: 'buy.vatChoice.supplierDocument', scopeFallback: 'Leverandørbilag', selectable: true,
      }, {
        value: 'import-basis:23', code: '23',
        labelKey: 'buy.vatChoice.importBasis23', fallback: 'Kode 23 · Grunnlag ved innførsel av varer (ingen MVA) (SAF-T 20)',
        contextKey: 'buy.vatChoice.importBasis23Context',
        contextFallback: 'Brukes på fakturaen fra utenlandsk selger når varen ville vært fritatt for MVA i Norge. Varebeløp og relevante tilleggsutgifter føres med samme behandling.',
        effectKey: 'buy.vatChoice.importBasis23Effect',
        effectFallback: 'Fakturaen bokføres som kostnad uten MVA og påvirker ikke MVA-meldingen. Eventuell tolldeklarasjon registreres separat med kode 85.',
        scopeKey: 'buy.vatChoice.supplierDocument', scopeFallback: 'Leverandørbilag', selectable: true,
      }],
    });
    return result;
  };

  const optionFor = (value, options = {}) => groups(options)
    .flatMap((group) => group.options)
    .find((option) => option.value === value) || null;

  const customsGroups = () => [{
    key: 'customs',
    labelKey: 'buy.vatChoice.customsGroup',
    fallback: 'Varer fra utlandet · tolldeklarasjon',
    options: CUSTOMS_CODES.map(([code, labelKey, fallback, contextKey, contextFallback, effectKey, effectFallback]) => ({
      value: `customs:${code}`,
      code,
      labelKey,
      fallback,
      contextKey,
      contextFallback,
      effectKey,
      effectFallback,
      scopeKey: 'buy.vatChoice.customsOnly',
      scopeFallback: 'Brukes bare på tolldeklarasjon',
      selectable: false,
      kind: 'customs',
    })),
  }];

  const grossFactor = ({ vatRate = 0, reverseChargeCode = '', vatTreatment = '' } = {}) => (
    String(reverseChargeCode || '') === REVERSE_CHARGE_CODE || ['vatOnly', 'importBasis'].includes(vatTreatment) ? 1 : 1 + Number(vatRate || 0) / 100
  );

  global.PlummePurchaseVatTreatment = { STANDARD_RATES, REVERSE_CHARGE_CODE, SPECIAL_CODES, resolve, valueFor, groups, customsGroups, optionFor, grossFactor };
})(typeof window !== 'undefined' ? window : globalThis);
